import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, ListGroup, Spinner, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const SearchSelectModal = ({
  show,
  onHide,
  title,
  placeholder,
  query,
  onQueryChange,
  loading = false,
  options = [],
  optionKey = (opt) => opt.id,
  optionLabel = (opt) => opt.name,
  optionSubLabel = (opt) => opt.composition,
  emptyMessage,
  loadingMessage,
  hint,
  onSelect,
  minQueryLength = 0,
  headerContent = null,
}) => {
  const { t } = useTranslation("layout");
  const resolvedTitle = title ?? t("searchSelectModal.title");
  const resolvedPlaceholder = placeholder ?? t("searchSelectModal.placeholder");
  const resolvedEmpty = emptyMessage ?? t("searchSelectModal.empty");
  const resolvedLoading = loadingMessage ?? t("searchSelectModal.loading");
  const [activeIndex, setActiveIndex] = useState(0);

  const isQueryEnough = (query || "").trim().length >= minQueryLength;

  const visibleOptions = useMemo(() => {
    if (!isQueryEnough) return [];
    return options || [];
  }, [options, isQueryEnough]);

  useEffect(() => {
    setActiveIndex(0);
  }, [show, query, visibleOptions.length]);

  const handleKeyDown = (e) => {
    if (!show) return;
    if (!isQueryEnough) return;
    if (!visibleOptions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visibleOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = visibleOptions[activeIndex];
      if (opt) onSelect(opt);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{resolvedTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body onKeyDown={handleKeyDown}>
        {headerContent ? (
          <div className="mb-3" onKeyDown={(e) => e.stopPropagation()}>
            {headerContent}
          </div>
        ) : null}
        <Form.Control
          autoFocus
          type="text"
          value={query}
          placeholder={resolvedPlaceholder}
          onChange={(e) => onQueryChange(e.target.value)}
        />

        {hint ? <div className="text-muted mt-2">{hint}</div> : null}

        <div className="mt-3">
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              <span>{resolvedLoading}</span>
            </div>
          ) : !isQueryEnough ? (
            <div className="text-muted">
              {t("searchSelectModal.minQuery", { count: minQueryLength })}
            </div>
          ) : visibleOptions.length > 0 ? (
            <ListGroup style={{ maxHeight: 360, overflowY: "auto" }}>
              {visibleOptions.map((opt, idx) => (
                <ListGroup.Item
                  key={optionKey(opt)}
                  action
                  active={idx === activeIndex}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => onSelect(opt)}
                >
                  <div className="fw-semibold">{optionLabel(opt)}</div>
                  {optionSubLabel(opt) ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      {optionSubLabel(opt)}
                    </div>
                  ) : null}
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-muted">{resolvedEmpty}</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("searchSelectModal.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SearchSelectModal;

